import { Button } from "@mui/material";

// The kit's own, so the trigger takes focus. MUI's Tooltip opens on
// hover and on focus, and a Chip, a Box or a Typography takes neither.
import Tooltip from "./Tooltip.jsx";
import { useColorScheme } from "@mui/material/styles";

/**
 * Light, dark, or the reader's own setting — cycled, not flipped.
 *
 * It is in the kit because it is the control that makes the theme's hardest
 * rule true, and a second app writing its own is a second app deciding what
 * "system" means. **Three states, not two.** `system` is the default and most
 * people never leave it, so a boolean toggle has nowhere to put the state the
 * README says is the one everybody forgets: the default, where neither
 * `data-theme` is stamped and only `prefers-color-scheme` separates the two
 * palettes. A two-state switch cannot return you to it, so the first click
 * silently opts a reader out of following their machine, permanently.
 *
 * ## The word, not only the shape
 *
 * The current mode is rendered as text. A sun-or-moon icon button is the
 * reflex and it fails the same rule a green dot fails: it has three states
 * and two glyphs, so `system` has to borrow one of them and the control then
 * says "light" while meaning "whatever your laptop says". The label is also
 * what makes the button announce something to a screen reader beyond
 * "button".
 *
 * `title` is the whole sentence, including what the next click does, because
 * a cycling control with no affordance is a control people click three times
 * to find out what it does.
 *
 * Nothing here imports an icon set. `@mui/icons-material` is a peer this
 * package deliberately does not have, and a kit component that dragged one in
 * would make the icon set a dependency of every app that renders a theme
 * switch.
 */

const NEXT = { dark: "light", light: "system", system: "dark" };

const SAYS = {
  system: "Following your system setting",
  light: "Light, chosen — not your system setting",
  dark: "Dark, chosen — not your system setting",
};

export default function SchemeToggle({ size = "small", sx }) {
  const { mode, setMode } = useColorScheme();

  // `undefined` until the provider has mounted and read storage. Rendering
  // "system" during that window would be a claim, and it would flicker into
  // something else a frame later.
  const current = mode || "system";
  const next = NEXT[current];
  const label = `${SAYS[current]}. Click for ${next}.`;

  return (
    <Tooltip title={label}>
      <Button
        size={size}
        color="inherit"
        onClick={() => setMode(next)}
        aria-label={label}
        sx={{ textTransform: "none", color: "text.secondary", ...sx }}
      >
        {current}
      </Button>
    </Tooltip>
  );
}
